import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Camera, X } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATIC_REVIEWS = [
  { title: "Un vrai plaisir au quotidien", text: "Je l'utilise tous les matins pendant que je prépare mon café. Je me sens vraiment plus tonique depuis que je l'ai.", author: "Sophie L." },
  { title: "Très facile à utiliser", text: "La livraison était rapide et la plateforme est exactement comme sur les photos. Elle ne prend pas beaucoup de place.", author: "Marie C." },
  { title: "Je recommande", text: "Les élastiques sont un vrai plus. Je peux faire une petite séance complète sans sortir de chez moi.", author: "Isabelle R." },
];

const stars = (n) => "★★★★★".slice(0, n) + "☆☆☆☆☆".slice(0, 5 - n);

export default function Reviews({ product }) {
  const [customerReviews, setCustomerReviews] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [photo, setPhoto] = useState(null);

  useEffect(() => { axios.get(`${API}/reviews`).then((r) => setCustomerReviews(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    const fd = new FormData(e.currentTarget);
    if (photo) fd.append("photo", photo);
    try {
      const r = await axios.post(`${API}/reviews`, fd);
      setCustomerReviews((list) => [r.data, ...list]);
      setFormOpen(false);
      setPhoto(null);
    } catch { alert("Impossible d'envoyer votre avis pour le moment."); }
    setSending(false);
  };

  return (
    <section className="vf-reviews" id="reviews">
      <div className="vf-reviews-inner">
        <Reveal className="vf-reviews-head">
          <div>
            <p className="vf-kicker">Ce que disent nos clientes</p>
            <h2 className="vf-h2">Des clientes<br /><em>conquises.</em></h2>
          </div>
          <div className="vf-score">
            <strong>{String(product.rating).replace(".", ",")}</strong>
            <span className="vf-stars">★★★★★</span>
            <small>{product.review_count} avis vérifiés</small>
            <button className="vf-review-cta" onClick={() => setFormOpen(true)} data-testid="open-review-form-button">Donner votre avis</button>
          </div>
        </Reveal>
        {customerReviews.length > 0 && (
          <div className="vf-review-cards vf-customer-reviews" data-testid="customer-reviews">
            {customerReviews.map((r) => (
              <Reveal key={r.id}>
                <article className="vf-review">
                  {r.photo_path && <img className="vf-review-photo" src={`${API}/files/${r.photo_path}`} alt={`Avis de ${r.name}`} />}
                  <span className="vf-stars">{stars(r.rating)}</span>
                  <h3>{r.title}</h3>
                  <p>{r.text}</p>
                  <b>— {r.name} <span>Avis client</span></b>
                </article>
              </Reveal>
            ))}
          </div>
        )}
        <div className="vf-review-cards">
          {STATIC_REVIEWS.map((r, i) => (
            <Reveal key={r.author} delay={i * 0.12}>
              <article className="vf-review">
                <span className="vf-stars">★★★★★</span>
                <h3>{r.title}</h3>
                <p>{r.text}</p>
                <b>— {r.author} <span>Achat vérifié</span></b>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {formOpen && (
          <motion.div className="vf-overlay vf-overlay-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setFormOpen(false)} data-testid="review-form-modal">
            <motion.div className="vf-modal" initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()}>
              <button className="vf-modal-close" onClick={() => setFormOpen(false)} data-testid="close-review-form-button" aria-label="Fermer"><X size={19} /></button>
              <p className="vf-kicker">Votre avis compte</p>
              <h2>Partagez votre expérience</h2>
              <form onSubmit={submit} data-testid="review-form">
                <input name="name" placeholder="Votre prénom" required data-testid="review-name-input" />
                <div className="vf-form-row">
                  <select name="rating" defaultValue="5" data-testid="review-rating-select" aria-label="Note">
                    <option value="5">★★★★★ (5/5)</option>
                    <option value="4">★★★★ (4/5)</option>
                    <option value="3">★★★ (3/5)</option>
                    <option value="2">★★ (2/5)</option>
                    <option value="1">★ (1/5)</option>
                  </select>
                  <input name="title" placeholder="Titre de votre avis" required data-testid="review-title-input" />
                </div>
                <textarea name="text" placeholder="Racontez votre expérience…" required rows={4} data-testid="review-text-input" />
                <label className="vf-photo-upload" data-testid="review-photo-label">
                  <Camera size={16} /> {photo ? photo.name : "Ajouter une photo (optionnel)"}
                  <input type="file" accept="image/*" hidden onChange={(e) => setPhoto(e.target.files[0] || null)} data-testid="review-photo-input" />
                </label>
                <button className="vf-cta" type="submit" disabled={sending} data-testid="review-submit-button">{sending ? "Envoi en cours…" : "Publier mon avis"}</button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

